import * as React from 'react';
import {CalendarDays, SquareX} from 'lucide-react';
import {cn} from '../../../lib/cn';

const DEFAULT_PLACEHOLDER = '00-00-0000';

type SingleDateInputProps = {
  id: string;
  name?: string;
  labelId: string;
  text: string;
  placeholder?: string;
  inputRef: React.RefObject<HTMLInputElement>;
  isEmpty: boolean;
  isFocused: boolean;
  isBlocked: boolean;
  isInvalid: boolean;
  loading?: boolean;
  readOnly?: boolean;
  hideClearDate?: boolean;
  hideCalendarIcon?: boolean;
  isOpen: boolean;
  clearLabel: string;
  openCalendarLabel: string;
  onTextChange: (text: string) => void;
  onFocus: React.FocusEventHandler<HTMLInputElement>;
  onBlur: () => void;
  onRowClick: () => void;
  onReset: (event: React.MouseEvent) => void;
  onToggleCalendar: () => void;
};

const inputBaseClass =
  'm-0 box-border h-full w-full min-w-0 border-0 bg-transparent text-[16px] font-medium leading-5 text-[var(--chekin-color-brand-navy)] outline-none placeholder:text-[length:var(--field-placeholder-font-size)] placeholder:font-[var(--field-placeholder-font-weight)] placeholder:text-[var(--chekin-color-gray-1)]';

const iconButtonClass =
  'flex h-5 w-5 items-center justify-center rounded-[3px] border-0 bg-transparent p-0 text-[var(--chekin-neutral-500)] outline-none hover:shadow-[0_3px_3px_#0f477734] disabled:cursor-not-allowed';

export function SingleDateInput({
  id,
  name,
  labelId,
  text,
  placeholder = DEFAULT_PLACEHOLDER,
  inputRef,
  isEmpty,
  isFocused,
  isBlocked,
  isInvalid,
  loading,
  readOnly,
  hideClearDate,
  hideCalendarIcon,
  isOpen,
  clearLabel,
  openCalendarLabel,
  onTextChange,
  onFocus,
  onBlur,
  onRowClick,
  onReset,
  onToggleCalendar,
}: SingleDateInputProps) {
  const inputClass = cn(
    inputBaseClass,
    isBlocked && 'cursor-not-allowed',
    loading && 'cursor-progress',
  );

  return (
    <div
      className={cn(
        'relative box-border flex h-12 w-full items-center rounded-[6px] pr-2 text-[16px] font-medium text-[var(--chekin-color-brand-navy)] transition-colors duration-200',
        isEmpty && !isFocused && 'bg-[var(--empty-field-background)]',
      )}
      onClick={onRowClick}
    >
      <input
        ref={inputRef}
        id={id}
        name={name}
        type="text"
        inputMode="numeric"
        autoComplete="off"
        placeholder={placeholder}
        value={text}
        disabled={isBlocked}
        readOnly={readOnly}
        aria-invalid={isInvalid}
        aria-labelledby={labelId}
        onChange={event => onTextChange(event.target.value)}
        onFocus={onFocus}
        onBlur={onBlur}
        className={cn(
          inputClass,
          'pl-[14px] pr-0',
          isEmpty && !isFocused && 'placeholder:invisible',
        )}
      />
      <span className="ml-auto flex shrink-0 items-center gap-2 pl-2 text-[var(--chekin-color-gray-2)]">
        {!readOnly && !hideClearDate && !isEmpty && (
          <button
            type="button"
            disabled={isBlocked}
            onClick={onReset}
            className={iconButtonClass}
            aria-label={clearLabel}
          >
            <SquareX
              size={16}
              fill="var(--chekin-neutral-500)"
              color="#f8f8f8"
              strokeWidth={1.8}
            />
          </button>
        )}
        {!readOnly && !hideCalendarIcon && (
          <button
            type="button"
            disabled={isBlocked}
            onClick={onToggleCalendar}
            aria-pressed={isOpen}
            className={cn(
              'flex h-6 w-6 items-center justify-center rounded-[3px] border-0 bg-transparent p-0 outline-none disabled:cursor-not-allowed',
              isFocused || isOpen
                ? 'text-[var(--chekin-color-brand-blue)]'
                : 'text-[var(--chekin-color-gray-2)]',
            )}
            aria-label={openCalendarLabel}
          >
            <CalendarDays size={18} />
          </button>
        )}
      </span>
    </div>
  );
}
